import React, { useState, useRef, useEffect } from "react";

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle option selection
  const toggleOption = (option) => {
    if (disabled) return;
    
    const newValue = value.includes(option)
      ? value.filter((v) => v !== option)
      : [...value, option];
    onChange(newValue);
  };

  // Remove selected option
  const removeOption = (option, e) => {
    e.stopPropagation();
    if (disabled) return;
    
    const newValue = value.filter((v) => v !== option);
    onChange(newValue);
  };

  // Clear all selections
  const clearAll = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  return (
    <div className={`multi-select ${className || ""}`} ref={dropdownRef} style={{ width: "100%" }}>
      <style>{`
        .multi-select {
          position: relative;
          width: 100%;
        }

        .multi-select__control {
          min-height: 38px;
          border: 1px solid #ced4da;
          border-radius: 6px;
          background-color: white;
          cursor: ${disabled ? "not-allowed" : "pointer"};
          padding: 4px 8px;
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          align-items: center;
          transition: border-color 0.2s, box-shadow 0.2s;
          opacity: ${disabled ? 0.6 : 1};
        }

        .multi-select__control:hover {
          border-color: ${disabled ? "#ced4da" : "#adb5bd"};
        }

        .multi-select__control--focused {
          border-color: #FA9411;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .multi-select__placeholder {
          color: #6c757d;
          padding: 6px 0;
          font-size: 14px;
        }

        .multi-select__value-container {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          flex: 1;
          min-width: 0;
        }

        .multi-select__chip {
          display: inline-flex;
          align-items: center;
          background-color: #FA9411;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          gap: 6px;
          max-width: 200px;
        }

        .multi-select__chip-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .multi-select__chip-remove {
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.3);
          transition: background-color 0.2s;
          font-size: 14px;
          line-height: 1;
          flex-shrink: 0;
        }

        .multi-select__chip-remove:hover {
          background-color: rgba(255, 255, 255, 0.5);
        }

        .multi-select__indicators {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-left: 8px;
        }

        .multi-select__clear {
          cursor: pointer;
          color: #6c757d;
          font-size: 18px;
          line-height: 1;
          padding: 2px;
          transition: color 0.2s;
        }

        .multi-select__clear:hover {
          color: #dc3545;
        }

        .multi-select__arrow {
          color: #6c757d;
          font-size: 12px;
          transition: transform 0.2s;
          transform: ${isOpen ? "rotate(180deg)" : "rotate(0deg)"};
        }

        .multi-select__menu {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 4px;
          background-color: white;
          border: 1px solid #ced4da;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          max-height: 300px;
          overflow: hidden;
          display: ${isOpen ? "block" : "none"};
        }

        .multi-select__search {
          padding: 8px 12px;
          border-bottom: 1px solid #e9ecef;
        }

        .multi-select__search-input {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }

        .multi-select__search-input:focus {
          border-color: #FA9411;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .multi-select__options {
          max-height: 240px;
          overflow-y: auto;
          padding: 4px 0;
        }

        .multi-select__option {
          padding: 8px 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
          font-size: 14px;
        }

        .multi-select__option:hover {
          background-color: #f8f9fa;
        }

        .multi-select__option--selected {
          background-color: #e7f3ff;
        }

        .multi-select__checkbox {
          width: 16px;
          height: 16px;
          border: 2px solid #ced4da;
          border-radius: 3px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.2s;
        }

        .multi-select__checkbox--checked {
          background-color: #FA9411;
          border-color: #FA9411;
        }

        .multi-select__checkbox-checkmark {
          color: white;
          font-size: 10px;
          font-weight: bold;
        }

        .multi-select__no-options {
          padding: 12px;
          text-align: center;
          color: #6c757d;
          font-size: 14px;
        }

        .multi-select__count {
          color: #6c757d;
          font-size: 12px;
          padding: 6px 0;
        }
      `}</style>

      <div
        className={`multi-select__control ${isOpen ? "multi-select__control--focused" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="multi-select__value-container">
          {value.length === 0 ? (
            <span className="multi-select__placeholder">{placeholder}</span>
          ) : (
            value.map((option) => (
              <span key={option} className="multi-select__chip">
                <span className="multi-select__chip-text" title={option}>
                  {option}
                </span>
                <span
                  className="multi-select__chip-remove"
                  onClick={(e) => removeOption(option, e)}
                  title="Remove"
                >
                  ×
                </span>
              </span>
            ))
          )}
        </div>
        <div className="multi-select__indicators">
          {value.length > 0 && !disabled && (
            <span
              className="multi-select__clear"
              onClick={clearAll}
              title="Clear all"
            >
              ×
            </span>
          )}
          <span className="multi-select__arrow">▼</span>
        </div>
      </div>

      {isOpen && (
        <div className="multi-select__menu">
          <div className="multi-select__search">
            <input
              type="text"
              className="multi-select__search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            />
          </div>
          <div className="multi-select__options">
            {filteredOptions.length === 0 ? (
              <div className="multi-select__no-options">
                {searchTerm ? "No options match your search" : "No options available"}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option);
                return (
                  <div
                    key={option}
                    className={`multi-select__option ${isSelected ? "multi-select__option--selected" : ""}`}
                    onClick={() => toggleOption(option)}
                  >
                    <div
                      className={`multi-select__checkbox ${isSelected ? "multi-select__checkbox--checked" : ""}`}
                    >
                      {isSelected && (
                        <span className="multi-select__checkbox-checkmark">✓</span>
                      )}
                    </div>
                    <span>{option}</span>
                  </div>
                );
              })
            )}
          </div>
          {value.length > 0 && (
            <div
              style={{
                padding: "8px 12px",
                borderTop: "1px solid #e9ecef",
                fontSize: "12px",
                color: "#6c757d",
                textAlign: "center",
              }}
            >
              {value.length} {value.length === 1 ? "item" : "items"} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;

